/**
 * §12.3 beat 2 on the player's device.
 *
 * The room sees the standings on the projector; a player looking down at their
 * phone should see the same thing, plus the one row the projector cannot
 * emphasize — their own. Rows carry the movement that produced them, which is
 * the part players actually watch for: who went up, and by how much.
 */

import { Avatar } from "@tq/ui";
import type { EventPayload } from "@tq/protocol";
import { ordinal } from "./StandingBar.js";

export interface PhoneLeaderboardProps {
  payload: EventPayload<"standings.published">;
  /** The viewing player, so their own row can be marked. */
  sessionPlayerId: string | null;
  /** Their standing, for the case where they are outside the top five. */
  standing: EventPayload<"player.standing.updated">;
}

/** §12.3 — the medals the mockup uses, then plain numbers. */
function medalFor(rank: number): string {
  if (rank === 1) return "👑";
  if (rank === 2) return "🥈";
  if (rank === 3) return "🥉";
  return String(rank);
}

export function PhoneLeaderboard({
  payload,
  sessionPlayerId,
  standing,
}: PhoneLeaderboardProps): React.ReactElement {
  const rows = payload.rows;
  const inTopRows = rows.some((row) => row.player.id === sessionPlayerId);

  return (
    <>
      <div className="tq-phone-lb-title">
        {payload.isFinal ? "🏁 Final Standings" : "🏆 Leaderboard — Top 5"}
      </div>

      <div className="tq-phone-lb">
        {rows.map((row) => {
          const climbed =
            row.previousRank != null && row.previousRank > row.rank
              ? row.previousRank - row.rank
              : 0;
          const mine = row.player.id === sessionPlayerId;
          return (
            <div
              key={row.player.id}
              className={`tq-phone-lb-row tq-phone-lb-row--rank${row.rank} ${mine ? "tq-phone-lb-row--mine" : ""}`}
            >
              <span className="tq-phone-lb-rank">{medalFor(row.rank)}</span>
              <Avatar
                photoUrl={row.player.photoUrl}
                displayName={row.player.displayName}
                size={30}
              />
              <span className="tq-phone-lb-name">{row.player.displayName}</span>
              {/* §12.4 — positive is upward, and it is the thing worth showing. */}
              {climbed > 0 && <span className="tq-phone-lb-move">▲{climbed}</span>}
              <span className="tq-phone-lb-score">{row.totalScore.toLocaleString()} pts</span>
            </div>
          );
        })}
      </div>

      {/* A player outside the top five still gets to see where they stand. */}
      {!inTopRows && standing.position != null && (
        <div className="tq-phone-lb-row tq-phone-lb-row--mine tq-phone-lb-row--outside">
          <span className="tq-phone-lb-rank">{standing.position}</span>
          <span className="tq-phone-lb-name">You</span>
          {standing.rankDelta != null && standing.rankDelta > 0 && (
            <span className="tq-phone-lb-move">▲{standing.rankDelta}</span>
          )}
          <span className="tq-phone-lb-score">{standing.totalScore.toLocaleString()} pts</span>
        </div>
      )}

      {!inTopRows && standing.position != null && standing.pointsToNextRank != null && (
        <p className="tq-phone-lb-next">
          {standing.pointsToNextRank.toLocaleString()} points from{" "}
          {ordinal(Math.max(1, standing.position - 1))}
        </p>
      )}
    </>
  );
}
